/**
 * File: PeerReviewTable.tsx
 * Description: Peer Review Checklist - a polished, management-facing view of the
 *   enterprise peer-review checklist. Items are grouped under boxed category
 *   banners. Each row is collapsible: the compact row shows Archie's verdict,
 *   the Architect Review and an approver check; expanding a row reveals a
 *   full-width conversational panel (Archie's recommendation + the Optimistic
 *   vs Pessimistic debate) where the architect can ask follow-up questions.
 * Author: GitHub Copilot
 */
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  PaperAirplaneIcon,
  PlusIcon,
  Squares2X2Icon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { AIDisclaimer } from '../../components/AIDisclaimer';
import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import {
  notifyPeerReviewChanged,
  PEER_REVIEW_CHANGED_EVENT,
} from '../../services/peerReviewEvents';
import { reviewService } from '../../services/reviewService';
import {
  PeerReviewArchieStatus,
  PeerReviewArchitectComment,
  PeerReviewFixResult,
  PeerReviewItem,
} from '../../types/review';

import { PeerReviewChatPanel } from './PeerReviewChatPanel';
import { PeerReviewProgressPanel } from './PeerReviewProgressPanel';

const BRAND_GREEN = '#008557';
const BRAND_GREEN_DARK = '#00614a';
const TABLE_COLUMN_COUNT = 6;
const LOAD_FAILED_MESSAGE = 'Failed to load the peer review checklist.';
const GENERATE_FAILED_MESSAGE =
  'Archie could not complete the peer review. Please try again.';
const GENERATE_INCOMPLETE_MESSAGE =
  "Archie's latest re-run did not finish. Showing the most recent " +
  'completed results.';
const CHECK_FAILED_MESSAGE = 'Failed to update the peer review check.';
const COMMENT_SAVE_FAILED_MESSAGE = 'Failed to add the comment.';
const ASK_FAILED_MESSAGE = 'Archie could not answer. Please try again.';
const FIX_FAILED_MESSAGE = 'Archie could not apply the fix. Please try again.';
const FIX_NO_CHANGES_MESSAGE =
  'Archie reviewed this item and did not find anything to change — the ' +
  'document already addresses it.';
const FIX_ALL_NONE_MESSAGE =
  'All items are already completed — there is nothing for Archie to fix.';
const ARCHIE_FIX_ALL_LABEL = 'Let Archie Fix All';
const ARCHIE_FIX_ALL_RUNNING_LABEL = 'Archie is fixing…';
const FIX_PROMPT_PLACEHOLDER =
  'Optional: tell Archie how to fix this item…';
const FIX_UNDO_FAILED_MESSAGE = 'Archie change could not be undone. Please try again.';
const FIX_KEEP_FAILED_MESSAGE = 'Archie change could not be kept. Please try again.';
const APPROVER_ONLY_TOOLTIP = 'Only approvers can confirm peer review items.';
const ARCHITECT_COMMENT_PLACEHOLDER = 'Write a comment…';
const ADD_COMMENT_LABEL = 'Add comment';
const POST_COMMENT_LABEL = 'Post';
const CANCEL_COMMENT_LABEL = 'Cancel';
const NO_COMMENTS_LABEL = 'No comments yet';
// "Custom Domain Checklist" authoring labels + messages. Must match the
// backend category name so custom items group under the same banner.
const CUSTOM_DOMAIN_CATEGORY = 'Custom Domain Checklist';
const CUSTOM_ITEM_PLACEHOLDER =
  'Describe a custom checklist item for this project, e.g. "Does the design ' +
  'follow our team\'s data retention policy?"';
const CUSTOM_ITEM_ADDED_MESSAGE =
  'Custom checklist item added. Re-run Archie Review to assess it.';
const CUSTOM_ITEM_ADD_FAILED_MESSAGE =
  'Could not add the custom checklist item. Please try again.';
const CUSTOM_ITEM_DELETE_FAILED_MESSAGE =
  'Could not remove the custom checklist item. Please try again.';
const CUSTOM_GUIDELINE_UPLOAD_FAILED_MESSAGE =
  'Could not upload the guideline document. Please try again.';
const CATEGORY_CHECK_TOOLTIP =
  'Check or uncheck every peer review item in this category.';
const ARCHIE_FIX_BUTTON_LABEL = 'Let Archie Fix This';
const ARCHIE_FIX_RUNNING_LABEL = 'Archie is fixing…';
const ARCHIE_ITEM_RERUN_LABEL = 'Re-run Archie Review';
const ARCHIE_ITEM_RERUN_RUNNING_LABEL = 'Reviewing…';
const ARCHIE_ITEM_RERUN_FAILED_MESSAGE =
  'Archie could not re-run this item. Please try again.';
const FIX_SUCCEEDED_TITLE = 'Archie Agent';
const FIX_NO_CHANGES_TITLE = 'No document changes needed';
const FIX_FAILED_TITLE = 'Archie could not apply changes';
const FIX_UNDONE_TITLE = 'Archie Agent — change undone';
const VIEW_SECTION_LABEL = 'View change in comments';
const KEEP_LABEL = 'Keep';
const UNDO_LABEL = 'Undo';
const FIX_STATUS_SUCCEEDED = 'succeeded';
// A fix the user undid: the document additions were removed, but the run is
// retained so the fix card stays visible (with the Undo decision recorded).
const FIX_STATUS_UNDONE = 'undone';
const ARCHIE_ACTION_KEEP_PREFIX = 'Keep Archie Agent';
const ARCHIE_ACTION_UNDO_PREFIX = 'Undo Archie Agent';
const CAN_BE_IMPROVED_LABEL = 'Can be improved';
const FOOTNOTE_MARKER_REGEX = /([A-Za-z])(\d+)(?=$|[^A-Za-z\d])/g;

type FixAction = 'keep' | 'undo';
type FixActionSelection = FixAction | null;

const normalizeCommentAuthor = (author?: string | null): string => {
  const trimmedAuthor = author?.trim();
  if (!trimmedAuthor) {
    return 'Shell User';
  }

  if (trimmedAuthor.includes('@')) {
    return trimmedAuthor;
  }

  const spacedAuthor = trimmedAuthor
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
    .trim();

  return spacedAuthor || trimmedAuthor;
};

const stripTrailingFootnoteMarkers = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const strippedValue = value.replace(
    FOOTNOTE_MARKER_REGEX,
    (_match, letter: string) => letter,
  );
  return strippedValue.endsWith('.') ? strippedValue : `${strippedValue}.`;
};

const FIX_STATUS_FAILED = 'failed';
const FIX_STATUS_NO_CHANGES = 'no_changes';

/**
 * Format a stored ISO timestamp for display under a comment bubble.
 */
const formatCommentTimestamp = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString();
};

interface StatusMeta {
  label: string;
  className: string;
  Icon: React.ComponentType<{ className?: string }>;
}

interface ArchitectReviewTimelineEntry {
  createdAt: string;
  type: 'archie-fix' | 'user-comment';
  key: string;
  comment?: PeerReviewArchitectComment;
  fix?: PeerReviewFixResult;
}

const ARCHIE_STATUS_META: Record<PeerReviewArchieStatus, StatusMeta> = {
  yes: {
    label: 'Completed',
    className: 'bg-green-100 text-green-700',
    Icon: CheckCircleIcon,
  },
  no: {
    label: CAN_BE_IMPROVED_LABEL,
    className: 'bg-amber-100 text-amber-800',
    Icon: XCircleIcon,
  },
  partial: {
    label: CAN_BE_IMPROVED_LABEL,
    className: 'bg-amber-100 text-amber-800',
    Icon: ExclamationTriangleIcon,
  },
  not_assessed: {
    label: CAN_BE_IMPROVED_LABEL,
    className: 'bg-gray-100 text-gray-500',
    Icon: MinusCircleIcon,
  },
};

// Auto-check is now persisted server-side (Archie sets peer_review_checked
// when it resolves or fixes an item), so the checklist renders the stored
// value directly - the approver's check/uncheck is authoritative and never
// re-forced client-side.

const buildArchieActionMessage = (action: 'keep' | 'undo'): string => {
  const actionLabel =
    action === 'keep' ? ARCHIE_ACTION_KEEP_PREFIX : ARCHIE_ACTION_UNDO_PREFIX;
  return `${actionLabel} Solution Summary improvement.`;
};

/**
 * The status to display for an item. A successful Archie fix promotes the item
 * to "Completed" (yes) - and it stays that way unless the user explicitly
 * undoes the fix. A "no changes needed" result also promotes it: Archie
 * reviewed the item and confirmed the document already addresses it, so it
 * cannot still be "Can be improved".
 */
const effectiveArchieStatus = (
  item: PeerReviewItem,
  selectedFixAction?: FixActionSelection,
): PeerReviewArchieStatus | null | undefined => {
  const fixStatus = item.archie_fix?.status;
  if (fixStatus === FIX_STATUS_SUCCEEDED && selectedFixAction !== 'undo') {
    return 'yes';
  }
  if (fixStatus === FIX_STATUS_NO_CHANGES) {
    return 'yes';
  }
  return item.archie_status;
};

const ARCHIE_KEEP_COMMENT_PREFIX = 'Keep Archie';

/**
 * True when a fix has been kept. Derived from the persisted "Keep" decision
 * comment so the kept state (Keep highlighted, Undo hidden) is consistent
 * across the checklist tab and the Full Document tab and survives a refresh.
 */
const isFixKept = (item: PeerReviewItem): boolean =>
  item.archie_fix?.status === FIX_STATUS_SUCCEEDED &&
  (item.architect_comments ?? []).some((comment) =>
    comment.message?.startsWith(ARCHIE_KEEP_COMMENT_PREFIX),
  );

/**
 * The keep/undo selection to display for an item. A persisted decision wins
 * over the optimistic local selection: an "undone" fix run shows Undo, a kept
 * fix shows Keep.
 */
const fixSelectionFor = (
  item: PeerReviewItem,
  localSelection: FixActionSelection,
): FixActionSelection => {
  if (item.archie_fix?.status === FIX_STATUS_UNDONE) {
    return 'undo';
  }
  return isFixKept(item) ? 'keep' : localSelection;
};

interface PeerReviewTableProps {
  reviewId: string;
  projectId: number;
  canApprove: boolean;
  canUseChat: boolean;
  canEditArchitectReview: boolean;
  canFix: boolean;
  onFixCompleted?: () => Promise<void> | void;
  onOpenFixSection?: (sectionName: string, sectionAnchor?: string) => void;
  onChecklistStatusChange?: (allChecked: boolean) => void;
  /** Show the "Submit for Review" button (the review hasn't been sent to
   *  human reviewers yet - the Archie-only phase). */
  showSubmitForReview?: boolean;
  /** Open the add-reviewers / submit flow. */
  onSubmitForReview?: () => void;
  /** Show the "Peer Review" approver check column. Only the Peer Review
   *  Checklist tab needs it; the Archie Review Checklist hides it. */
  showPeerReviewColumn?: boolean;
  /** Presentation mode. 'peer-approval' is the approver's read-only sign-off
   *  view: it strips Archie's tooling (recommendation, debate chat, and the
   *  Fix All / Re-run buttons) down to results + comments + the check column. */
  variant?: 'archie-review' | 'peer-approval';
}

/**
 * Compact summary chip used in the header bar.
 */
/**
 * Parse a backend timestamp to epoch ms. Backend datetimes are stored as
 * naive UTC, so a timezone-less ISO string is interpreted as UTC (otherwise JS
 * would treat it as local time and the displayed time would be hours off).
 */
const parseBackendTimestamp = (value: string): number => {
  const hasTimezone = /[zZ]|(?:[+-]\d{2}:?\d{2}$)/.test(value);
  return Date.parse(hasTimezone ? value : `${value}Z`);
};

/**
 * The most recent Archie generation time across items, as epoch ms (0 if none).
 */
const maxArchieGeneratedAt = (items: PeerReviewItem[]): number => {
  let max = 0;
  for (const item of items) {
    if (item.archie_generated_at) {
      const time = parseBackendTimestamp(item.archie_generated_at);
      if (!Number.isNaN(time) && time > max) {
        max = time;
      }
    }
  }
  return max;
};

// The "Last fixed" stamp under the "Let Archie Fix All" button tracks when the
// master batch run finished - not the individual per-row fix times. It is
// captured client-side when the batch completes and persisted per review so it
// survives a refresh (individual single-row fixes do not update it).
const FIX_ALL_COMPLETED_AT_KEY = 'peerReview:fixAllCompletedAt';

const loadFixAllCompletedAt = (reviewId: string): number | null => {
  try {
    const raw = globalThis.localStorage.getItem(
      `${FIX_ALL_COMPLETED_AT_KEY}:${reviewId}`,
    );
    if (!raw) {
      return null;
    }
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

const saveFixAllCompletedAt = (reviewId: string, epochMs: number): void => {
  try {
    globalThis.localStorage.setItem(
      `${FIX_ALL_COMPLETED_AT_KEY}:${reviewId}`,
      String(epochMs),
    );
  } catch {
    // localStorage may be unavailable; the timestamp is non-critical.
  }
};

/** Format an epoch-ms timestamp as a short, human-readable date + time. */
const formatReviewedAt = (epochMs: number): string =>
  new Date(epochMs).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const StatusChip: React.FC<{
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  className: string;
}> = ({ Icon, label, value, className }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${className}`}
  >
    <Icon className="h-4 w-4" />
    <span className="font-bold">{value}</span>
    {label}
  </span>
);

/**
 * Category-level bulk-check control with indeterminate state.
 */
const CategoryCheckControl: React.FC<{
  category: string;
  checked: boolean;
  indeterminate: boolean;
  canApprove: boolean;
  isSaving: boolean;
  onToggle: () => void;
}> = ({
  category,
  checked,
  indeterminate,
  canApprove,
  isSaving,
  onToggle,
}) => {
  const checkboxRef = useRef<HTMLInputElement | null>(null);
  const labelClassName =
    'ml-auto flex items-center gap-2 rounded-lg border border-gray-200 ' +
    `bg-white px-3 py-1.5 text-xs font-medium text-gray-600 ${
      canApprove && !isSaving ? 'cursor-pointer' : 'cursor-not-allowed'
    }`;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <label className={labelClassName}>
      <input
        ref={checkboxRef}
        type="checkbox"
        checked={checked}
        disabled={!canApprove || isSaving}
        aria-label={`Set all peer review checks for category: ${category}`}
        title={canApprove ? CATEGORY_CHECK_TOOLTIP : APPROVER_ONLY_TOOLTIP}
        onChange={onToggle}
        className="h-4 w-4 rounded border-gray-300 text-[#008557] focus:ring-[#008557]"
      />
      <span>{isSaving ? 'Saving…' : 'Check all'}</span>
    </label>
  );
};

/**
 * Resolve the header presentation (title, colours, icon) for a fix outcome.
 */
const getFixPresentation = (
  status: string,
): {
  title: string;
  tone: string;
  iconColor: string;
  Icon: React.ComponentType<{ className?: string }>;
} => {
  if (status === FIX_STATUS_SUCCEEDED) {
    return {
      title: FIX_SUCCEEDED_TITLE,
      tone: 'border-[#008557]/30 bg-[#008557]/5',
      iconColor: 'text-[#008557]',
      Icon: SparklesIcon,
    };
  }
  if (status === FIX_STATUS_FAILED) {
    return {
      title: FIX_FAILED_TITLE,
      tone: 'border-amber-300 bg-amber-50',
      iconColor: 'text-amber-600',
      Icon: ExclamationTriangleIcon,
    };
  }
  if (status === FIX_STATUS_UNDONE) {
    return {
      title: FIX_UNDONE_TITLE,
      tone: 'border-gray-200 bg-gray-50',
      iconColor: 'text-gray-500',
      Icon: SparklesIcon,
    };
  }
  return {
    title: FIX_NO_CHANGES_TITLE,
    tone: 'border-gray-200 bg-gray-50',
    iconColor: 'text-gray-500',
    Icon: SparklesIcon,
  };
};

/**
 * One changed-section row inside the Archie fix summary card.
 */
const FixChangeRow: React.FC<{
  sectionName: string;
  title: string;
  description: string;
  sectionAnchor?: string;
  onOpenSection?: (sectionName: string, sectionAnchor?: string) => void;
}> = ({
  sectionName,
  title,
  description,
  sectionAnchor,
  onOpenSection,
}) => (
  <li className="rounded-md border border-gray-200 bg-white px-2 py-1.5">
    <div className="flex items-center gap-1">
      <DocumentTextIcon className="h-3.5 w-3.5 flex-shrink-0 text-[#008557]" />
      <span className="text-[11px] font-semibold text-gray-700">{title}</span>
    </div>
    {description && (
      <p className="mt-0.5 text-[11px] leading-snug text-gray-600">
        {description}
      </p>
    )}
    {onOpenSection && (
      <button
        type="button"
        onClick={() => onOpenSection(sectionName, sectionAnchor)}
        aria-label={`${VIEW_SECTION_LABEL}: ${title}`}
        className="mt-2 inline-flex items-center gap-1 rounded-md border border-[#008557]/30 bg-[#008557]/10 px-2 py-1 text-[10px] font-semibold text-[#00614a] transition-colors hover:bg-[#008557]/15"
      >
        <DocumentTextIcon className="h-3 w-3" />
        {VIEW_SECTION_LABEL}
      </button>
    )}
  </li>
);

/**
 * Keep / Undo decision for an applied Archie fix. Keep confirms the change
 * (highlighted text stays); Undo removes Archie's additions and reverts the
 * verdict. Rendered next to the fix result in the Archie Review column and
 * mirrored in the Full Document view - both read the same persisted decision.
 */
const FixDecisionButtons: React.FC<{
  onKeep?: () => void;
  onUndo?: () => void;
  isApplyingAction?: boolean;
  selectedAction?: FixActionSelection;
}> = ({ onKeep, onUndo, isApplyingAction = false, selectedAction = null }) => (
  <div className="flex items-center gap-2">
    {selectedAction !== 'undo' && (
      <button
        type="button"
        disabled={isApplyingAction || selectedAction === 'keep'}
        onClick={onKeep}
        aria-label={KEEP_LABEL}
        title={KEEP_LABEL}
        className={`inline-flex items-center justify-center rounded-md border p-1.5 transition-colors ${
          selectedAction === 'keep'
            ? 'border-[#008557] bg-[#008557] text-white'
            : 'border-[#008557]/30 bg-white text-[#00614a] hover:bg-[#008557]/5'
        }`}
      >
        <CheckIcon className="h-4 w-4" />
      </button>
    )}
    {selectedAction !== 'keep' && (
      <button
        type="button"
        disabled={isApplyingAction || selectedAction === 'undo'}
        onClick={onUndo}
        aria-label={UNDO_LABEL}
        title={UNDO_LABEL}
        className={`inline-flex items-center justify-center rounded-md border p-1.5 transition-colors ${
          selectedAction === 'undo'
            ? 'border-red-500 bg-red-500 text-white'
            : 'border-gray-300 bg-white text-gray-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600'
        }`}
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    )}
  </div>
);

/**
 * Card summarizing the outcome of a "Let Archie Fix This" run for a row. The
 * Keep/Undo decision lives here, in the Architect Review column; the card (the
 * record of what Archie changed) stays visible regardless of the decision, and
 * the chosen action is recorded as a "Keep"/"Undo" architect comment.
 */
const ArchieFixSummary: React.FC<{
  fix: PeerReviewFixResult;
  onOpenSection?: (sectionName: string, sectionAnchor?: string) => void;
  onKeep?: () => void;
  onUndo?: () => void;
  isApplyingAction?: boolean;
  selectedAction?: FixActionSelection;
}> = ({
  fix,
  onOpenSection,
  onKeep,
  onUndo,
  isApplyingAction = false,
  selectedAction = null,
}) => {
  const { title, tone, iconColor, Icon } = getFixPresentation(fix.status);
  const timestamp = formatCommentTimestamp(
    fix.completed_at ?? fix.created_at ?? '',
  );
  const isDecidedStatus =
    fix.status === FIX_STATUS_SUCCEEDED || fix.status === FIX_STATUS_UNDONE;
  const hasDecisionHandlers = Boolean(onKeep || onUndo);
  return (
    <div className={`mt-2 rounded-lg border p-2.5 ${tone}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Icon className={`h-4 w-4 flex-shrink-0 ${iconColor}`} />
          <span className="text-xs font-semibold text-gray-800">{title}</span>
        </div>
        {timestamp && (
          <span className="flex-shrink-0 text-[10px] text-gray-500">
            {timestamp}
          </span>
        )}
      </div>
      {fix.summary && (
        <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-gray-600">
          {fix.summary}
        </p>
      )}
      {fix.changes.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1.5">
          {fix.changes.map((change, index) => (
            <FixChangeRow
              key={`${change.section_name}-${index}`}
              sectionName={change.section_name}
              title={change.section_title || change.section_name}
              description={change.change_description}
              sectionAnchor={change.section_anchor}
              onOpenSection={onOpenSection}
            />
          ))}
        </ul>
      )}
      {isDecidedStatus && hasDecisionHandlers && (
        <div className="mt-2">
          <FixDecisionButtons
            onKeep={onKeep}
            onUndo={onUndo}
            isApplyingAction={isApplyingAction}
            selectedAction={selectedAction}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Display the Archie verdict pill + short explanation for a checklist item,
 * the "Let Archie Fix This" action and the summary of any fix Archie applied.
 * The full debate now lives in the expandable chat panel.
 */
const ArchieReviewCell: React.FC<{
  item: PeerReviewItem;
  isGenerating: boolean;
  isDebating: boolean;
  canFix: boolean;
  isFixing: boolean;
  isRerunning: boolean;
  onFix: (item: PeerReviewItem, userPrompt?: string) => void;
  onRerun: (item: PeerReviewItem) => void;
  selectedFixAction?: FixActionSelection;
  fixPrompt?: string;
  onFixPromptChange?: (itemId: number, value: string) => void;
}> = ({
  item,
  isGenerating,
  isDebating,
  canFix,
  isFixing,
  isRerunning,
  onFix,
  onRerun,
  selectedFixAction = null,
  fixPrompt = '',
  onFixPromptChange,
}) => {
  const status = effectiveArchieStatus(item, selectedFixAction);

  if (isGenerating && !status) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <ArrowPathIcon className="w-4 h-4 animate-spin" />
        Archie is reviewing…
      </div>
    );
  }

  // While the debate agents are thinking/answering, show a debating state on
  // the verdict instead of the (possibly now-stale) badge.
  if (isDebating) {
    return (
      <div className="flex items-center gap-2 text-sm text-indigo-600">
        <ArrowPathIcon className="w-4 h-4 animate-spin" />
        Agents debating…
      </div>
    );
  }

  const meta = status ? ARCHIE_STATUS_META[status] : null;
  const StatusIcon = meta?.Icon;
  const badgeClass =
    'inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 ' +
    `text-xs font-semibold ${meta?.className ?? ''}`;
  // Hide the numeric verdict score once a fix has promoted the item to
  // "Completed" - the score reflects the original (pre-fix) assessment.
  const showScore =
    typeof item.archie_score === 'number' && status === item.archie_status;
  // Only offer "Let Archie Fix This" once there is actually something to fix:
  // Archie's review has run (a verdict exists) AND a recommendation is
  // available. Until then only the "Re-run Archie Review" action is shown.
  const showFix = Boolean(status) && Boolean(item.archie_recommendation);
  return (
    <div className="flex flex-col gap-2">
      {meta && StatusIcon ? (
        <span className={badgeClass}>
          <StatusIcon className="w-3.5 h-3.5" />
          {meta.label}
          {showScore && (
            <span className="ml-1 font-normal">({item.archie_score}/100)</span>
          )}
        </span>
      ) : (
        <span className="text-sm text-gray-400">—</span>
      )}
      {status &&
        (() => {
          // When a fix promoted the item to "Completed", the original debate
          // answer ("...disagree - review recommended") is stale - show a
          // fix-consistent line instead (different copy for "no changes
          // needed", where Archie added nothing because the document already
          // addresses it).
          const answerText = (() => {
            if (status === item.archie_status) {
              return item.archie_answer;
            }
            if (item.archie_fix?.status === FIX_STATUS_NO_CHANGES) {
              return 'Archie reviewed this item and the document already '
                + 'addresses it — marked completed.';
            }
            return 'Archie applied an improvement to the document and marked '
              + 'this item completed.';
          })();
          return answerText ? (
            <p className="text-xs leading-snug text-gray-600 line-clamp-3">
              {answerText}
            </p>
          ) : null;
        })()}
      {canFix && (
        <div className="flex flex-col items-stretch gap-1.5 pt-1">
          <div className="flex items-stretch gap-1.5">
            <button
              type="button"
              disabled={isRerunning || isFixing}
              onClick={() => onRerun(item)}
              title={ARCHIE_ITEM_RERUN_LABEL}
              className="inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 rounded-md border border-[#008557] px-2 py-1.5 text-center text-xs font-semibold leading-tight text-[#008557] shadow-sm transition-colors hover:bg-[#008557]/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ArrowPathIcon
                className={`h-3.5 w-3.5 flex-shrink-0 ${
                  isRerunning ? 'animate-spin' : ''
                }`}
              />
              <span className="break-words">
                {isRerunning
                  ? ARCHIE_ITEM_RERUN_RUNNING_LABEL
                  : ARCHIE_ITEM_RERUN_LABEL}
              </span>
            </button>
            {showFix && (
              <button
                type="button"
                disabled={isFixing || isRerunning}
                onClick={() => onFix(item, fixPrompt)}
                title={ARCHIE_FIX_BUTTON_LABEL}
                className="inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 rounded-md bg-[#008557] px-2 py-1.5 text-center text-xs font-semibold leading-tight text-white shadow-sm transition-colors hover:bg-[#00614a] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isFixing ? (
                  <>
                    <ArrowPathIcon className="h-3.5 w-3.5 flex-shrink-0 animate-spin" />
                    <span className="break-words">
                      {ARCHIE_FIX_RUNNING_LABEL}
                    </span>
                  </>
                ) : (
                  <>
                    <WrenchScrewdriverIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="break-words">
                      {ARCHIE_FIX_BUTTON_LABEL}
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
          {showFix && (
            <textarea
              value={fixPrompt}
              onChange={(event) =>
                onFixPromptChange?.(item.checklist_item_id, event.target.value)
              }
              placeholder={FIX_PROMPT_PLACEHOLDER}
              rows={2}
              disabled={isFixing || isRerunning}
              className="w-full resize-none rounded-md border border-gray-300 px-2 py-1.5 text-[11px] leading-snug text-gray-700 placeholder:text-gray-400 focus:border-[#008557] focus:outline-none focus:ring-1 focus:ring-[#008557] disabled:opacity-60"
            />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * A single architect comment rendered as a chat bubble (author + timestamp).
 */
const ArchitectCommentBubble: React.FC<{
  comment: PeerReviewArchitectComment;
}> = ({ comment }) => {
  const timestamp = formatCommentTimestamp(comment.created_at);
  const authorName = normalizeCommentAuthor(
    comment.author_display_name ?? comment.author,
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[11px] font-semibold text-gray-700">
          {authorName}
        </span>
        {timestamp && (
          <span className="flex-shrink-0 text-[10px] text-gray-400">{timestamp}</span>
        )}
      </div>
      <p className="mt-0.5 whitespace-pre-wrap break-words text-xs leading-snug text-gray-700">
        {comment.message}
      </p>
    </div>
  );
};

const ArchieFixCommentCard: React.FC<{
  fix: PeerReviewFixResult;
  onOpenSection?: (sectionName: string, sectionAnchor?: string) => void;
  onKeep?: () => void;
  onUndo?: () => void;
  isApplyingAction?: boolean;
  selectedAction?: FixActionSelection;
}> = ({
  fix,
  onOpenSection,
  onKeep,
  onUndo,
  isApplyingAction = false,
  selectedAction = null,
}) => {
  const normalizedFix: PeerReviewFixResult = {
    ...fix,
    summary: stripTrailingFootnoteMarkers(fix.summary),
  };

  return (
    <ArchieFixSummary
      fix={normalizedFix}
      onOpenSection={onOpenSection}
      onKeep={onKeep}
      onUndo={onUndo}
      isApplyingAction={isApplyingAction}
      selectedAction={selectedAction}
    />
  );
};

/**
 * Inline composer for adding a new architect comment.
 */
const ArchitectCommentComposer: React.FC<{
  itemLabel: string;
  isSaving: boolean;
  onSubmit: (message: string) => void;
  onCancel: () => void;
}> = ({ itemLabel, isSaving, onSubmit, onCancel }) => {
  const [draft, setDraft] = useState('');
  const trimmed = draft.trim();

  return (
    <div className="flex flex-col gap-1.5">
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        rows={2}
        autoFocus
        placeholder={ARCHITECT_COMMENT_PLACEHOLDER}
        aria-label={`New comment for: ${itemLabel}`}
        className="w-full resize-y rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 shadow-sm transition-colors focus:border-[#008557] focus:outline-none focus:ring-1 focus:ring-[#008557]"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={isSaving || !trimmed}
          onClick={() => onSubmit(trimmed)}
          className="inline-flex items-center gap-1 rounded-md bg-[#008557] px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-[#00614a] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? (
            <ArrowPathIcon className="h-3 w-3 animate-spin" />
          ) : (
            <PaperAirplaneIcon className="h-3 w-3" />
          )}
          {POST_COMMENT_LABEL}
        </button>
        <button
          type="button"
          disabled={isSaving}
          onClick={onCancel}
          className="rounded-md px-2 py-1 text-[11px] font-medium text-gray-500 transition-colors hover:text-gray-700 disabled:opacity-50"
        >
          {CANCEL_COMMENT_LABEL}
        </button>
      </div>
    </div>
  );
};

/**
 * Architect review thread for a checklist item: a scrollable list of comment
 * bubbles plus an inline composer (opened with the “+” button) so each review
 * participant can leave their own timestamped comment.
 */
const ArchitectReviewCell: React.FC<{
  item: PeerReviewItem;
  canComment: boolean;
  isSaving: boolean;
  onAddComment: (item: PeerReviewItem, message: string) => Promise<boolean>;
  onOpenFixSection?: (sectionName: string, sectionAnchor?: string) => void;
  onFixAction?: (item: PeerReviewItem, action: 'keep' | 'undo') => Promise<void>;
  selectedFixAction?: FixActionSelection;
  isApplyingFixAction?: boolean;
}> = ({
  item,
  canComment,
  isSaving,
  onAddComment,
  onOpenFixSection,
  onFixAction,
  selectedFixAction = null,
  isApplyingFixAction = false,
}) => {
  const [composing, setComposing] = useState(false);
  const comments = item.architect_comments ?? [];
  const timelineEntries: ArchitectReviewTimelineEntry[] = [
    ...(item.archie_fix &&
    (item.archie_fix.status === FIX_STATUS_SUCCEEDED ||
      item.archie_fix.status === FIX_STATUS_UNDONE)
      ? [
        {
          createdAt: item.archie_fix.completed_at ?? item.archie_fix.created_at ?? '',
          type: 'archie-fix' as const,
          key: `archie-fix-${item.checklist_item_id}`,
          fix: item.archie_fix,
        },
      ]
      : []),
    ...comments.map((comment, index) => ({
      createdAt: comment.created_at,
      type: 'user-comment' as const,
      key: `${comment.created_at}-${index}`,
      comment,
    })),
  ].sort(
    (first, second) =>
      new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime(),
  );

  const handleSubmit = async (message: string) => {
    const succeeded = await onAddComment(item, message);
    if (succeeded) {
      setComposing(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {timelineEntries.length > 0 ? (
        <div className="flex max-h-40 flex-col gap-1.5 overflow-y-auto pr-1">
          {timelineEntries.map((entry) => {
            if (entry.type === 'archie-fix' && entry.fix) {
              return (
                <ArchieFixCommentCard
                  key={entry.key}
                  fix={entry.fix}
                  onOpenSection={onOpenFixSection}
                  onKeep={
                    canComment && onFixAction
                      ? () => {
                        onFixAction(item, 'keep').catch(() => {
                          /* Errors are surfaced by the handler. */
                        });
                      }
                      : undefined
                  }
                  onUndo={
                    canComment && onFixAction
                      ? () => {
                        onFixAction(item, 'undo').catch(() => {
                          /* Errors are surfaced by the handler. */
                        });
                      }
                      : undefined
                  }
                  isApplyingAction={isApplyingFixAction}
                  selectedAction={selectedFixAction}
                />
              );
            }

            return entry.comment ? (
              <ArchitectCommentBubble
                key={entry.key}
                comment={entry.comment}
              />
            ) : null;
          })}
        </div>
      ) : (
        !composing && (
          <span className="text-xs text-gray-400">{NO_COMMENTS_LABEL}</span>
        )
      )}

      {canComment && composing && (
        <ArchitectCommentComposer
          itemLabel={item.checklist_item}
          isSaving={isSaving}
          onSubmit={handleSubmit}
          onCancel={() => setComposing(false)}
        />
      )}

      {canComment && !composing && (
        <button
          type="button"
          onClick={() => setComposing(true)}
          aria-label={`Add a comment for: ${item.checklist_item}`}
          className="inline-flex w-fit items-center gap-1 rounded-md border border-dashed border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-500 transition-colors hover:border-[#008557] hover:text-[#008557]"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {ADD_COMMENT_LABEL}
        </button>
      )}
    </div>
  );
};

/**
 * Approver-only confirmation check for a checklist item.
 */
const PeerReviewCheckCell: React.FC<{
  item: PeerReviewItem;
  canApprove: boolean;
  isSaving: boolean;
  onToggle: (item: PeerReviewItem) => void;
}> = ({ item, canApprove, isSaving, onToggle }) => {
  const checked = item.peer_review_checked;
  return (
    <button
      type="button"
      disabled={!canApprove || isSaving}
      onClick={() => onToggle(item)}
      title={canApprove ? undefined : APPROVER_ONLY_TOOLTIP}
      aria-pressed={checked}
      aria-label={`Peer review check for: ${item.checklist_item}`}
      className={`flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
        checked ? 'border-green-600 bg-green-50' : 'border-gray-300 bg-white'
      } ${
        canApprove && !isSaving
          ? 'cursor-pointer hover:border-green-500'
          : 'cursor-not-allowed opacity-60'
      }`}
    >
      {checked ? (
        <CheckCircleIcon className="h-6 w-6 text-green-600" />
      ) : (
        <span className="h-3.5 w-3.5 rounded-full border border-gray-300" />
      )}
    </button>
  );
};

/**
 * Boxed, collapsible banner introducing a category group.
 */
const CategoryBanner: React.FC<{
  category: string;
  collapsed: boolean;
  allChecked: boolean;
  partiallyChecked: boolean;
  canApprove: boolean;
  isSaving: boolean;
  onToggle: () => void;
  onToggleCheckAll: () => void;
  showPeerReviewColumn?: boolean;
}> = ({
  category,
  collapsed,
  allChecked,
  partiallyChecked,
  canApprove,
  isSaving,
  onToggle,
  onToggleCheckAll,
  showPeerReviewColumn = true,
}) => (
  <tr>
    <td colSpan={TABLE_COLUMN_COUNT} className="px-0 pb-2 pt-6">
      <div
        className="flex items-center gap-3 rounded-lg border px-4 py-2.5 transition-colors hover:brightness-95"
        style={{
          borderColor: `${BRAND_GREEN}33`,
          backgroundColor: `${BRAND_GREEN}0d`,
        }}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="flex flex-1 items-center gap-3 text-left"
        >
          {collapsed ? (
            <ChevronRightIcon
              className="h-4 w-4 flex-shrink-0"
              style={{ color: BRAND_GREEN_DARK }}
            />
          ) : (
            <ChevronDownIcon
              className="h-4 w-4 flex-shrink-0"
              style={{ color: BRAND_GREEN_DARK }}
            />
          )}
          <span
            className="flex h-7 w-7 items-center justify-center rounded-md text-white"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            <Squares2X2Icon className="h-4 w-4" />
          </span>
          <h3
            className="text-sm font-bold uppercase tracking-wide"
            style={{ color: BRAND_GREEN_DARK }}
          >
            {category}
          </h3>
        </button>
        {showPeerReviewColumn && (
          <CategoryCheckControl
            category={category}
            checked={allChecked}
            indeterminate={partiallyChecked}
            canApprove={canApprove}
            isSaving={isSaving}
            onToggle={onToggleCheckAll}
          />
        )}
      </div>
    </td>
  </tr>
);

/**
 * Determine whether a row begins a new category group.
 */
const isFirstInCategory = (
  items: PeerReviewItem[],
  index: number,
): boolean => index === 0 || items[index].category !== items[index - 1].category;

/**
 * Return all items for a category.
 */
const itemsInCategory = (
  items: PeerReviewItem[],
  category: string,
): PeerReviewItem[] => items.filter((item) => item.category === category);

/**
 * The "Let Archie Fix All" action is unavailable until Archie has run, and
 * while a fix batch or a generation is already in flight.
 */
const isFixAllDisabled = (
  analysisDone: boolean,
  fixingAll: boolean,
  generating: boolean,
): boolean => !analysisDone || fixingAll || generating;

/**
 * PeerReviewTable - enterprise peer review checklist with Archie verdicts and
 * an editable architect review.
 */
export const PeerReviewTable: React.FC<PeerReviewTableProps> = ({
  reviewId,
  projectId,
  canApprove,
  canUseChat,
  canEditArchitectReview,
  canFix,
  onFixCompleted,
  onOpenFixSection,
  onChecklistStatusChange,
  showSubmitForReview = false,
  onSubmitForReview,
  showPeerReviewColumn = true,
  variant = 'archie-review',
}) => {
  const isPeerApproval = variant === 'peer-approval';
  const [items, setItems] = useState<PeerReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingItemId, setSavingItemId] = useState<number | null>(null);
  const [savingCategory, setSavingCategory] = useState<string | null>(null);
  const [savingArchitectId, setSavingArchitectId] = useState<number | null>(
    null,
  );
  const [fixingItemId, setFixingItemId] = useState<number | null>(null);
  const [rerunningItemId, setRerunningItemId] = useState<number | null>(null);
  const [fixingAll, setFixingAll] = useState(false);
  const [fixAllProgress, setFixAllProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  // When the most recent "Let Archie Fix All" batch finished (epoch ms),
  // persisted per review so the "Last fixed" stamp survives a refresh.
  const [fixAllCompletedAt, setFixAllCompletedAt] = useState<number | null>(
    () => loadFixAllCompletedAt(reviewId),
  );
  // Per-row custom fix instructions, keyed by checklist item id.
  const [fixPrompts, setFixPrompts] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [hasInitializedExpandedItems, setHasInitializedExpandedItems] =
    useState(false);
  const [autoRequestedRecommendationIds, setAutoRequestedRecommendationIds] =
    useState<Set<number>>(new Set());
  const [askingItemId, setAskingItemId] = useState<number | null>(null);
  const [recommendingItemId, setRecommendingItemId] = useState<number | null>(
    null,
  );
  const [applyingFixActionItemId, setApplyingFixActionItemId] =
    useState<number | null>(null);
  const [fixActionSelections, setFixActionSelections] = useState<
    Record<number, FixActionSelection>
  >({});
  // "Custom Domain Checklist" authoring state (owner-editable views only).
  const [customDraft, setCustomDraft] = useState('');
  const [pendingGuidelines, setPendingGuidelines] = useState<string[]>([]);
  const [addingCustomItem, setAddingCustomItem] = useState(false);
  const [uploadingGuideline, setUploadingGuideline] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<number | null>(null);
  const guidelineInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    setExpandedItems(new Set());
    setHasInitializedExpandedItems(false);
    setAutoRequestedRecommendationIds(new Set());
    setFixAllCompletedAt(loadFixAllCompletedAt(reviewId));
  }, [reviewId]);

  // Mirror the latest items into a ref so runArchie can compare the previous
  // generation time without re-creating the callback on every items change.
  const itemsRef = useRef<PeerReviewItem[]>([]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const runArchie = useCallback(async () => {
    setGenerating(true);
    setError(null);
    setNotice(null);
    const previousReviewedAt = maxArchieGeneratedAt(itemsRef.current);
    try {
      const result = await reviewService.generatePeerReview(reviewId);
      setItems(result.items);
    } catch {
      // The debate can outlive the client poll, or fail intermittently while
      // the previous results stay persisted. Re-check the saved checklist
      // before surfacing a hard error (which would otherwise vanish on a
      // manual refresh and confuse the user).
      try {
        const refreshed = await reviewService.getPeerReview(reviewId);
        setItems(refreshed.items);
        if (maxArchieGeneratedAt(refreshed.items) > previousReviewedAt) {
          // Fresh verdicts actually landed: the run completed, so no error.
          setError(null);
        } else if (refreshed.archie_generated) {
          setError(GENERATE_INCOMPLETE_MESSAGE);
        } else {
          setError(GENERATE_FAILED_MESSAGE);
        }
      } catch {
        setError(GENERATE_FAILED_MESSAGE);
      }
    } finally {
      setGenerating(false);
    }
  }, [reviewId]);

  const handleAddCustomItem = useCallback(async () => {
    const description = customDraft.trim();
    if (!description) {
      return;
    }
    setAddingCustomItem(true);
    setError(null);
    setNotice(null);
    try {
      const result = await reviewService.addCustomChecklistItem(
        reviewId,
        description,
        pendingGuidelines,
      );
      setItems(result.items);
      setCustomDraft('');
      setPendingGuidelines([]);
      setNotice(CUSTOM_ITEM_ADDED_MESSAGE);
    } catch {
      setError(CUSTOM_ITEM_ADD_FAILED_MESSAGE);
    } finally {
      setAddingCustomItem(false);
    }
  }, [reviewId, customDraft, pendingGuidelines]);

  const handleUploadGuideline = useCallback(
    async (file: File) => {
      setUploadingGuideline(true);
      setError(null);
      setNotice(null);
      try {
        const result = await reviewService.uploadCustomGuideline(
          reviewId,
          file,
        );
        setPendingGuidelines((current) =>
          current.includes(result.name)
            ? current
            : [...current, result.name],
        );
        setNotice(result.message);
      } catch {
        setError(CUSTOM_GUIDELINE_UPLOAD_FAILED_MESSAGE);
      } finally {
        setUploadingGuideline(false);
      }
    },
    [reviewId],
  );

  const handleDeleteCustomItem = useCallback(
    async (checklistItemId: number) => {
      setDeletingItemId(checklistItemId);
      setError(null);
      try {
        const result = await reviewService.deleteCustomChecklistItem(
          reviewId,
          checklistItemId,
        );
        setItems(result.items);
      } catch {
        setError(CUSTOM_ITEM_DELETE_FAILED_MESSAGE);
      } finally {
        setDeletingItemId(null);
      }
    },
    [reviewId],
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await reviewService.getPeerReview(reviewId);
        if (cancelled) {
          return;
        }
        setItems(result.items);
        // The checklist structure is ready: stop the full-screen spinner and
        // render the table now. The first-time Archie run continues under the
        // `generating` flag, which shows the progress panel instead of a blank
        // screen.
        setLoading(false);
        // Auto-run Archie the first time the checklist is opened.
        if (!result.archie_generated) {
          await runArchie();
        }
      } catch {
        if (!cancelled) {
          setError(LOAD_FAILED_MESSAGE);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [reviewId, runArchie]);

  // Re-fetch when a Keep/Undo (or fix) happens in another peer-review view
  // (e.g. the Full Document tab) so the checklist stays in sync.
  useEffect(() => {
    const handler = (): void => {
      reviewService
        .getPeerReview(reviewId)
        .then((result) => setItems(result.items))
        .catch(() => {
          /* Non-fatal: a manual refresh will reconcile. */
        });
    };
    globalThis.addEventListener(PEER_REVIEW_CHANGED_EVENT, handler);
    return () =>
      globalThis.removeEventListener(PEER_REVIEW_CHANGED_EVENT, handler);
  }, [reviewId]);

  const handleToggle = useCallback(
    async (item: PeerReviewItem) => {
      setSavingItemId(item.checklist_item_id);
      setError(null);
      try {
        const result = await reviewService.setPeerReviewCheck(
          reviewId,
          item.checklist_item_id,
          !item.peer_review_checked,
        );
        setItems(result.items);
      } catch {
        setError(CHECK_FAILED_MESSAGE);
      } finally {
        setSavingItemId(null);
      }
    },
    [reviewId],
  );

  const getCategoryCheckState = useCallback(
    (category: string) => {
      const categoryItems = itemsInCategory(items, category);
      const allChecked =
        categoryItems.length > 0 &&
        categoryItems.every((item) => item.peer_review_checked);
      const partiallyChecked =
        !allChecked &&
        categoryItems.some((item) => item.peer_review_checked);
      return { allChecked, partiallyChecked };
    },
    [items],
  );

  const handleAddArchitectComment = useCallback(
    async (item: PeerReviewItem, message: string): Promise<boolean> => {
      setSavingArchitectId(item.checklist_item_id);
      setError(null);
      try {
        const result = await reviewService.addArchitectComment(
          reviewId,
          item.checklist_item_id,
          message,
        );
        setItems(result.items);
        return true;
      } catch {
        setError(COMMENT_SAVE_FAILED_MESSAGE);
        return false;
      } finally {
        setSavingArchitectId(null);
      }
    },
    [reviewId],
  );

  const handleFix = useCallback(
    async (item: PeerReviewItem, userPrompt?: string) => {
      setFixingItemId(item.checklist_item_id);
      setError(null);
      setNotice(null);
      try {
        const result = await reviewService.fixPeerReview(
          reviewId,
          item.checklist_item_id,
          userPrompt?.trim() || undefined,
        );
        setItems(result.items);
        // The job can succeed while the fix run itself made no changes (the
        // item already meets the guidance) or failed internally. Surface that
        // outcome so the action does not silently appear to "do nothing".
        const fixed = result.items.find(
          (current) => current.checklist_item_id === item.checklist_item_id,
        );
        const fixStatus = fixed?.archie_fix?.status;
        if (fixStatus === FIX_STATUS_NO_CHANGES) {
          setNotice(FIX_NO_CHANGES_MESSAGE);
        } else if (fixStatus === FIX_STATUS_FAILED) {
          setError(FIX_FAILED_MESSAGE);
        }
        await onFixCompleted?.();
      } catch {
        setError(FIX_FAILED_MESSAGE);
      } finally {
        setFixingItemId((current) =>
          current === item.checklist_item_id ? null : current,
        );
      }
    },
    [onFixCompleted, reviewId],
  );

  // Re-run the Archie peer review for a single row (enterprise or custom),
  // without re-running the whole checklist.
  const handleRerunItem = useCallback(
    async (item: PeerReviewItem) => {
      setRerunningItemId(item.checklist_item_id);
      setError(null);
      setNotice(null);
      try {
        const result = await reviewService.rerunPeerReviewItem(
          reviewId,
          item.checklist_item_id,
        );
        setItems(result.items);
      } catch {
        setError(ARCHIE_ITEM_RERUN_FAILED_MESSAGE);
      } finally {
        setRerunningItemId((current) =>
          current === item.checklist_item_id ? null : current,
        );
      }
    },
    [reviewId],
  );

  // "Let Archie Fix All": run a fix for every item that still has a gap, one
  // at a time, with determinate progress. There is no overall timeout - each
  // fix is a background job and the batch simply proceeds item by item.
  const handleFixAll = useCallback(async () => {
    const targets = itemsRef.current.filter((item) => {
      const status = effectiveArchieStatus(item);
      return status === 'partial' || status === 'no';
    });
    if (targets.length === 0) {
      setNotice(FIX_ALL_NONE_MESSAGE);
      return;
    }
    setFixingAll(true);
    setError(null);
    setNotice(null);
    setFixAllProgress({ done: 0, total: targets.length });
    try {
      for (let index = 0; index < targets.length; index += 1) {
        setFixAllProgress({ done: index, total: targets.length });
        setFixingItemId(targets[index].checklist_item_id);
        try {
          const result = await reviewService.fixPeerReview(
            reviewId,
            targets[index].checklist_item_id,
          );
          setItems(result.items);
        } catch {
          // Keep going so one failure does not abort the whole batch.
        }
      }
      setFixAllProgress({ done: targets.length, total: targets.length });
      await onFixCompleted?.();
      // Stamp the master run's finish time (not the individual fix times).
      const completedAt = Date.now();
      saveFixAllCompletedAt(reviewId, completedAt);
      setFixAllCompletedAt(completedAt);
    } finally {
      setFixingAll(false);
      setFixingItemId(null);
      setFixAllProgress(null);
    }
  }, [onFixCompleted, reviewId]);

  const handleFixPromptChange = useCallback((itemId: number, value: string) => {
    setFixPrompts((previous) => ({ ...previous, [itemId]: value }));
  }, []);

  const handleFixAction = useCallback(
    async (item: PeerReviewItem, action: 'keep' | 'undo') => {
      if (!item.archie_fix) {
        return;
      }

      setApplyingFixActionItemId(item.checklist_item_id);
      setFixActionSelections((currentSelections) => ({
        ...currentSelections,
        [item.checklist_item_id]: action,
      }));
      setError(null);

      try {
        // Undo removes Archie's additions from the document and reverts the
        // verdict; Keep simply records the decision as an architect comment
        // (the document keeps the green changes). Both are authored
        // server-side, consistently with every other comment.
        const result =
          action === 'undo'
            ? await reviewService.undoPeerReviewFix(
              reviewId,
              item.checklist_item_id,
            )
            : await reviewService.addArchitectComment(
              reviewId,
              item.checklist_item_id,
              buildArchieActionMessage('keep'),
            );
        setItems(result.items);
        // Refresh the Full Document view and any other peer-review view.
        await onFixCompleted?.();
        notifyPeerReviewChanged();
      } catch {
        setError(
          action === 'keep' ? FIX_KEEP_FAILED_MESSAGE : FIX_UNDO_FAILED_MESSAGE,
        );
        setFixActionSelections((currentSelections) => ({
          ...currentSelections,
          [item.checklist_item_id]: null,
        }));
      } finally {
        setApplyingFixActionItemId(null);
      }
    },
    [onFixCompleted, reviewId],
  );

  const handleToggleCategoryCheck = useCallback(
    async (category: string) => {
      const categoryItems = itemsInCategory(items, category);
      const allChecked =
        categoryItems.length > 0 &&
        categoryItems.every((item) => item.peer_review_checked);
      const nextChecked = !allChecked;
      const itemsToUpdate = categoryItems.filter(
        (item) => item.peer_review_checked !== nextChecked,
      );

      if (itemsToUpdate.length === 0) {
        return;
      }

      setSavingCategory(category);
      setError(null);
      try {
        for (const item of itemsToUpdate) {
          await reviewService.setPeerReviewCheck(
            reviewId,
            item.checklist_item_id,
            nextChecked,
          );
        }
        const refreshed = await reviewService.getPeerReview(reviewId);
        setItems(refreshed.items);
      } catch {
        setError(CHECK_FAILED_MESSAGE);
      } finally {
        setSavingCategory((current) =>
          current === category ? null : current,
        );
      }
    },
    [items, reviewId],
  );

  // Lazily synthesise Archie's recommendation the first time a row is opened.
  const requestRecommendation = useCallback(
    async (itemId: number) => {
      setRecommendingItemId(itemId);
      try {
        const result = await reviewService.recommendPeerReview(
          reviewId,
          itemId,
        );
        setItems(result.items);
      } catch {
        // A missing recommendation is non-fatal; the panel shows an empty
        // state and the reviewer can still ask questions.
      } finally {
        setRecommendingItemId((current) =>
          current === itemId ? null : current,
        );
      }
    },
    [reviewId],
  );

  const handleAsk = useCallback(
    async (itemId: number, question: string) => {
      setAskingItemId(itemId);
      setError(null);
      try {
        const result = await reviewService.askPeerReview(
          reviewId,
          itemId,
          question,
        );
        setItems(result.items);
      } catch {
        setError(ASK_FAILED_MESSAGE);
      } finally {
        setAskingItemId((current) => (current === itemId ? null : current));
      }
    },
    [reviewId],
  );

  const toggleRow = useCallback(
    (item: PeerReviewItem) => {
      const itemId = item.checklist_item_id;
      const willExpand = !expandedItems.has(itemId);
      setExpandedItems((previous) => {
        const next = new Set(previous);
        if (next.has(itemId)) {
          next.delete(itemId);
        } else {
          next.add(itemId);
        }
        return next;
      });
      // On first expand, generate the recommendation if Archie has run but
      // no recommendation exists yet.
      if (
        willExpand &&
        item.archie_status &&
        !item.archie_recommendation &&
        recommendingItemId !== itemId
      ) {
        requestRecommendation(itemId).catch(() => {
          /* Errors are surfaced by the handler. */
        });
      }
    },
    [expandedItems, recommendingItemId, requestRecommendation],
  );

  // Report to the parent whether every item has been confirmed (all green).
  useEffect(() => {
    if (!onChecklistStatusChange) {
      return;
    }
    const allChecked =
      items.length > 0 && items.every((item) => item.peer_review_checked);
    onChecklistStatusChange(allChecked);
  }, [items, onChecklistStatusChange]);

  useEffect(() => {
    // The approver view has no expandable debate, so never auto-expand there.
    if (isPeerApproval || hasInitializedExpandedItems || items.length === 0) {
      return;
    }

    const firstItemId = items[0]?.checklist_item_id;
    if (!firstItemId) {
      return;
    }

    setExpandedItems(new Set([firstItemId]));
    setHasInitializedExpandedItems(true);
  }, [hasInitializedExpandedItems, isPeerApproval, items]);

  useEffect(() => {
    // No recommendation panel in the approver view - skip the auto-synthesis.
    if (isPeerApproval) {
      return;
    }

    const firstItem = items[0];
    if (!firstItem) {
      return;
    }

    const firstItemId = firstItem.checklist_item_id;
    if (!expandedItems.has(firstItemId)) {
      return;
    }
    if (!firstItem.archie_status || firstItem.archie_recommendation) {
      return;
    }
    if (autoRequestedRecommendationIds.has(firstItemId)) {
      return;
    }

    setAutoRequestedRecommendationIds((previous) => {
      const next = new Set(previous);
      next.add(firstItemId);
      return next;
    });
    requestRecommendation(firstItemId).catch(() => {
      /* Errors are surfaced by the handler. */
    });
  }, [
    autoRequestedRecommendationIds,
    expandedItems,
    isPeerApproval,
    items,
    requestRecommendation,
  ]);

  const toggleCategory = useCallback((category: string) => {
    setCollapsedCategories((previous) => {
      const next = new Set(previous);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const gapsCount = items.filter((item) => {
    const status = effectiveArchieStatus(
      item,
      fixActionSelections[item.checklist_item_id],
    );
    return status === 'partial' || status === 'no';
  }).length;
  const completedCount = items.filter(
    (item) =>
      effectiveArchieStatus(
        item,
        fixActionSelections[item.checklist_item_id],
      ) === 'yes',
  ).length;
  const lastReviewedAt = maxArchieGeneratedAt(items);
  const analysisDone = items.some((item) => Boolean(item.archie_status));

  return (
    <div className="space-y-5">
      {/* Header / toolbar */}
      <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <span
            className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            <SparklesIcon className="h-6 w-6" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isPeerApproval ? 'Peer Review Checklist' : 'Archie Review Checklist'}
            </h2>
            <p className="text-xs text-gray-500">
              {isPeerApproval
                ? 'Final approval sign-off. Only the assigned approver can check off items.'
                : 'Enterprise checklist assessed by Archie. Managed by the project owner.'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {canFix && (
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={handleFixAll}
                disabled={isFixAllDisabled(analysisDone, fixingAll, generating)}
                title={
                  analysisDone
                    ? undefined
                    : 'Run the Archie review first'
                }
                className="flex items-center gap-2 rounded-md bg-[#008557] px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#00614a] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <WrenchScrewdriverIcon
                  className={`h-4 w-4 ${fixingAll ? 'animate-pulse' : ''}`}
                />
                {fixingAll
                  ? ARCHIE_FIX_ALL_RUNNING_LABEL
                  : ARCHIE_FIX_ALL_LABEL}
              </button>
              {!fixingAll && fixAllCompletedAt && (
                <span className="text-[11px] text-gray-500">
                  Last fixed {formatReviewedAt(fixAllCompletedAt)}
                </span>
              )}
            </div>
          )}
          <div className="flex flex-col items-stretch gap-1.5">
            <StatusChip
              Icon={CheckCircleIcon}
              label="Completed"
              value={completedCount}
              className="bg-green-50 text-green-700"
            />
            <StatusChip
              Icon={ExclamationTriangleIcon}
              label="Gaps"
              value={gapsCount}
              className="bg-amber-50 text-amber-700"
            />
          </div>
          {/* Action buttons (submit + re-run) are hidden in the approver's
              read-only peer-approval sign-off view. */}
          {!isPeerApproval && (
            <div className="flex flex-col items-stretch gap-1">
              {showSubmitForReview && onSubmitForReview && (
                <button
                  type="button"
                  onClick={onSubmitForReview}
                  title="Add reviewers and submit this architecture for peer review"
                  className="main-button flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                  Submit for Review
                </button>
              )}
              <button
                type="button"
                onClick={runArchie}
                disabled={generating || fixingAll}
                className="flex items-center justify-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowPathIcon
                  className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`}
                />
                {generating ? 'Archie is reviewing…' : 'Re-run Archie Review'}
              </button>
              {!generating && lastReviewedAt > 0 && (
                <span className="text-center text-[11px] text-gray-500">
                  Last reviewed {formatReviewedAt(lastReviewedAt)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* The approver sign-off view omits the AI-generated disclaimer. */}
      {!isPeerApproval && <AIDisclaimer />}

      {generating && <PeerReviewProgressPanel itemCount={items.length} />}

      {fixingAll && fixAllProgress && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="relative flex h-10 w-10 items-center justify-center">
              <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/30" />
              <WrenchScrewdriverIcon className="h-5 w-5 animate-pulse text-emerald-600" />
            </span>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900">
                Archie is applying fixes
              </h3>
              <p className="mt-0.5 text-xs text-gray-500">
                Fixing item{' '}
                {Math.min(fixAllProgress.done + 1, fixAllProgress.total)} of{' '}
                {fixAllProgress.total}…
              </p>
            </div>
            <span className="shrink-0 text-xs font-medium text-gray-600">
              {fixAllProgress.done}/{fixAllProgress.total}
            </span>
          </div>
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500 ease-out"
              style={{
                width: `${
                  fixAllProgress.total > 0
                    ? (fixAllProgress.done / fixAllProgress.total) * 100
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {error && <ErrorMessage message={error} />}

      {notice && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
          <SparklesIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
          <span>{notice}</span>
        </div>
      )}

      {/* Custom Domain Checklist authoring panel. Owner-editable views only
          (hidden in the approver's read-only sign-off). Placed above the
          checklist because the custom category renders as the first category.
          Lets the architect extend this project's checklist with their own
          items and upload a custom guideline document indexed for the review
          agent. */}
      {!isPeerApproval && canFix && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <span
              className="flex h-8 w-8 items-center justify-center rounded-md text-white"
              style={{ backgroundColor: BRAND_GREEN }}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </span>
            <div>
              <h3
                className="text-sm font-bold uppercase tracking-wide"
                style={{ color: BRAND_GREEN_DARK }}
              >
                {CUSTOM_DOMAIN_CATEGORY}
              </h3>
              <p className="text-xs text-gray-500">
                Extend this project&apos;s checklist with your own items. Archie
                assesses each custom item against the generated architecture and
                any uploaded guideline, then gives a verdict, recommendation and
                debate — just like the enterprise items.
              </p>
            </div>
          </div>

          <textarea
            value={customDraft}
            onChange={(event) => setCustomDraft(event.target.value)}
            placeholder={CUSTOM_ITEM_PLACEHOLDER}
            rows={3}
            className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-[#008557] focus:outline-none focus:ring-1 focus:ring-[#008557]"
          />

          {pendingGuidelines.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {pendingGuidelines.map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700"
                >
                  <DocumentTextIcon className="h-3.5 w-3.5" />
                  {name}
                </span>
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleAddCustomItem}
              disabled={!customDraft.trim() || addingCustomItem}
              className="flex items-center gap-2 rounded-md bg-[#008557] px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#00614a] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4" />
              {addingCustomItem ? 'Adding…' : 'Add checklist item'}
            </button>
            <input
              ref={guidelineInputRef}
              type="file"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  handleUploadGuideline(file);
                }
                event.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => guidelineInputRef.current?.click()}
              disabled={uploadingGuideline}
              className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowUpTrayIcon
                className={`h-4 w-4 ${uploadingGuideline ? 'animate-pulse' : ''}`}
              />
              {uploadingGuideline ? 'Uploading…' : 'Upload Guideline'}
            </button>
          </div>
        </div>
      )}

      {/* Checklist */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            <col className="w-[4%]" />
            <col className="w-[10%]" />
            <col className="w-[23%]" />
            <col className="w-[24%]" />
            <col className={showPeerReviewColumn ? 'w-[31%]' : 'w-[39%]'} />
            {showPeerReviewColumn && <col className="w-[8%]" />}
          </colgroup>
          <thead className="sticky top-0 z-10 bg-gray-50/90 backdrop-blur">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              <th className="px-2 py-3">
                <span className="sr-only">Expand</span>
              </th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Checklist Item</th>
              <th className="px-4 py-3">Archie Review</th>
              <th className="px-4 py-3">Architect Review</th>
              {showPeerReviewColumn && (
                <th className="px-4 py-3 text-center">Peer Review</th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const collapsed = collapsedCategories.has(item.category);
              const expanded = expandedItems.has(item.checklist_item_id);
              const debateCount = item.archie_debate?.length ?? 0;
              const { allChecked, partiallyChecked } =
                getCategoryCheckState(item.category);
              return (
                <React.Fragment key={item.checklist_item_id}>
                  {isFirstInCategory(items, index) && (
                    <CategoryBanner
                      category={item.category}
                      collapsed={collapsed}
                      allChecked={allChecked}
                      partiallyChecked={partiallyChecked}
                      canApprove={canApprove}
                      isSaving={savingCategory === item.category}
                      onToggle={() => toggleCategory(item.category)}
                      onToggleCheckAll={() => {
                        handleToggleCategoryCheck(item.category).catch(() => {
                          /* Errors are surfaced by the handler. */
                        });
                      }}
                      showPeerReviewColumn={showPeerReviewColumn}
                    />
                  )}
                  {!collapsed && (
                    <tr
                      className={`border-t border-gray-100 align-top transition-colors hover:bg-gray-50/60 ${
                        expanded ? 'bg-indigo-50/30' : ''
                      }`}
                    >
                      <td className="px-2 py-3">
                        {/* No expand/debate in the approver's read-only view. */}
                        {!isPeerApproval && (
                          <button
                            type="button"
                            onClick={() => toggleRow(item)}
                            aria-expanded={expanded}
                            aria-label={
                              expanded ? 'Collapse item' : 'Expand item'
                            }
                            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                          >
                            {expanded ? (
                              <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-gray-500">
                        {item.sub_category ?? ''}
                      </td>
                      <td className="px-4 py-3">
                        {isPeerApproval ? (
                          <span className="text-sm leading-snug text-gray-800">
                            {item.checklist_item}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleRow(item)}
                            className="flex w-full flex-col items-start gap-1 text-left"
                          >
                            <span className="text-sm leading-snug text-gray-800">
                              {item.checklist_item}
                            </span>
                            {debateCount > 0 && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600">
                                <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                                {debateCount}{' '}
                                {debateCount === 1 ? 'message' : 'messages'} ·{' '}
                                {expanded ? 'hide' : 'open'} discussion
                              </span>
                            )}
                          </button>
                        )}
                        {item.is_custom &&
                          (item.enterprise_guidance?.length ?? 0) > 0 && (
                          <div className="mt-2 flex w-full min-w-0 flex-col gap-1.5">
                            {item.enterprise_guidance.map((name) => (
                              <span
                                key={name}
                                title={name}
                                className="flex min-w-0 items-start gap-1.5 rounded-md border border-emerald-100 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-800"
                              >
                                <DocumentTextIcon className="mt-[1px] h-3.5 w-3.5 flex-shrink-0 text-[#008557]" />
                                <span className="min-w-0 break-words">
                                  <span className="text-emerald-600">
                                    Guideline:
                                  </span>{' '}
                                  {name}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                        {item.is_custom && canFix && !isPeerApproval && (
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteCustomItem(item.checklist_item_id)
                            }
                            disabled={
                              deletingItemId === item.checklist_item_id
                            }
                            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-red-600 transition-colors hover:text-red-700 disabled:opacity-50"
                          >
                            <XMarkIcon className="h-3.5 w-3.5" />
                            {deletingItemId === item.checklist_item_id
                              ? 'Removing…'
                              : 'Remove custom item'}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <ArchieReviewCell
                          item={item}
                          isGenerating={generating}
                          isDebating={askingItemId === item.checklist_item_id}
                          canFix={canFix}
                          isFixing={fixingItemId === item.checklist_item_id}
                          isRerunning={
                            rerunningItemId === item.checklist_item_id
                          }
                          onFix={handleFix}
                          onRerun={handleRerunItem}
                          selectedFixAction={fixSelectionFor(
                            item,
                            fixActionSelections[item.checklist_item_id] ?? null,
                          )}
                          fixPrompt={fixPrompts[item.checklist_item_id] ?? ''}
                          onFixPromptChange={handleFixPromptChange}
                        />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <ArchitectReviewCell
                          item={item}
                          canComment={canEditArchitectReview}
                          isSaving={savingArchitectId === item.checklist_item_id}
                          onAddComment={handleAddArchitectComment}
                          onOpenFixSection={onOpenFixSection}
                          // Keep/Undo is the owner's fix decision (Archie Review
                          // Checklist tab only). The approver view still allows
                          // comments, but not fix decisions - so omit the action.
                          onFixAction={isPeerApproval ? undefined : handleFixAction}
                          selectedFixAction={fixSelectionFor(
                            item,
                            fixActionSelections[item.checklist_item_id] ?? null,
                          )}
                          isApplyingFixAction={
                            applyingFixActionItemId === item.checklist_item_id
                          }
                        />
                      </td>
                      {showPeerReviewColumn && (
                        <td className="px-4 py-3 align-top">
                          <div className="flex justify-center">
                            <PeerReviewCheckCell
                              item={item}
                              canApprove={canApprove}
                              isSaving={savingItemId === item.checklist_item_id}
                              onToggle={handleToggle}
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  )}
                  {!collapsed && expanded && !isPeerApproval && (
                    <tr className="border-t border-gray-100 bg-indigo-50/20">
                      {/* Spacer: align the detail with the start of the
                          Checklist Item column (skip expand + category). */}
                      <td colSpan={2} className="p-0" />
                      {/* Recommendation (compact) on top, the agent debate
                          below - spanning across to the Architect Review
                          column without overlapping the inline comments. */}
                      <td
                        colSpan={showPeerReviewColumn ? 4 : 3}
                        className="px-4 pb-5 pt-1 align-top"
                      >
                        <PeerReviewChatPanel
                          checklistItemId={item.checklist_item_id}
                          projectId={projectId}
                          messages={item.archie_debate ?? []}
                          recommendation={item.archie_recommendation}
                          recommendationLoading={
                            recommendingItemId === item.checklist_item_id
                          }
                          asking={askingItemId === item.checklist_item_id}
                          canAsk={canUseChat && Boolean(item.archie_status)}
                          onAsk={(question) =>
                            handleAsk(item.checklist_item_id, question)
                          }
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={TABLE_COLUMN_COUNT}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  No peer review checklist items found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
